import React, { useState, useMemo } from 'react';
import { SelectRVToolsFile } from './components/SelectRVToolsFile';
import { SheetDataViewer } from '../components/SheetDataViewer';
import { ReviewContents } from './components/ReviewContents';
import { SendingToInventory } from '../components/SendingToInventory';
import { ImportHistoryPanel } from '../components/ImportHistoryPanel';
import { REQUIRED_CSV_FILES, REQUIRED_EXCEL_SHEETS, IMPORT_TYPES } from '../../../../constants/discovery';

import { Typography, LoaderAnimation, Form, Steps } from '../../../../components';

const { Paragraph, Title } = Typography;

import './RVTools.scss';

export const RVTools = () => {
    const [fileList, setFileList] = useState([]);
    const [step, setStep] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [activeImportId, setActiveImportId] = useState(null);
    const [resetCount, setResetCount] = useState(0);
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);

    const [form] = Form.useForm();

    const completeStep = () => {
        setStep(step + 1);
    };

    const resetForm = () => {
        setIsUploading(false);
        setFileList([]);
        setStep(0);
        setResetCount((count) => count + 1);
    };

    const handleDelete = () => {
        setFileList([]);
        setStep(0);
    };

    const stepItems = useMemo(
        () => [
            {
                title: 'Select File',
                content: step === 0 ? 'Select RVTools zip file' : undefined,
            },
            {
                title: 'Extract CSV Files',
            },
            {
                title: 'Review',
                content: step === 2 ? 'Review your data before importing' : undefined,
            },
            {
                title: 'Adding to Inventory',
            },
        ],
        [step],
    );

    return (
        <div className="discovery-rvtools">
            <div className="rvtools-page-header">
                <Title level={2} className="rvtools-page-title">
                    Import New RVTools Dump
                </Title>
                <Paragraph>
                    Upload your RV Tools CSV or Excel export to populate your Inventory. We&apos;ll analyze your VMs and
                    hosts to surface insights on capacity, performance, and rightsizing potential.
                </Paragraph>
            </div>
            <div>
                <div className="csv-upload-dialog">
                    <div className="rv-tools-left-col-upload">
                        <Steps orientation="vertical" current={step} items={stepItems} />
                    </div>
                    <div className="rv-tools-right-col-upload">
                        {step === 0 && (
                            <SelectRVToolsFile
                                key={resetCount}
                                resetForm={resetForm}
                                fileList={fileList}
                                setFileList={setFileList}
                                handleDelete={handleDelete}
                                completeStep={completeStep}
                                form={form}
                            />
                        )}
                        {step === 1 && <LoaderAnimation size="small" color="grayscale" />}
                        {step === 2 && (
                            <ReviewContents
                                form={form}
                                orgId={orgId}
                                setStep={setStep}
                                setActiveImportId={setActiveImportId}
                                fileList={fileList}
                                resetForm={resetForm}
                                isUploading={isUploading}
                                setIsUploading={setIsUploading}
                            />
                        )}
                        {step === 3 && <SendingToInventory setStep={setStep} activeImportId={activeImportId} />}
                    </div>
                </div>
                {[1, 2].includes(step) && !isUploading && (
                    <SheetDataViewer
                        file={fileList[0]?.originFileObj}
                        requiredCsvFiles={REQUIRED_CSV_FILES}
                        excelSheetsToDisplay={REQUIRED_EXCEL_SHEETS}
                        setStep={setStep}
                        emptyStateText="No CSV data found"
                    />
                )}
                {!isUploading && <ImportHistoryPanel orgId={orgId} importType={IMPORT_TYPES.rvTools} />}
                {isUploading && <LoaderAnimation color="blueBackground" size="small" />}
            </div>
        </div>
    );
};
